// ============================================================================
//  MEDIA CHAT BAR COMPONENT - MOBILE OPTIMIZED

// ============================================================================
// Displays voice AND video chat controls during poker game
// Shows mute/unmute, video on/off, deafen, leave, participant count, and video grid
//
//  FEATURES:

// - Uses participant.username from Firestore mapping (populated by useAgoraMedia)
// - Uses participant.odid to lookup from usernames prop as fallback
// - Uses participant.uid for React keys (Agora UID - always unique)
// - Uses participant.isSpeaking for speaking indicators
// - Gets current user's username from usernames[odid] prop
// - Green glow on speaking participants
// - 🆕 BACKGROUND BLUR & VIRTUAL BACKGROUNDS
// - 🆕 MOBILE: Top video strip, right-side collapsible controls
// - 🆕 MOBILE: Minimize/expand functionality
// - 🆕 MOBILE: Usernames always visible
// ============================================================================

import React, { useState, useEffect, useRef, useCallback } from 'react';
import useAgoraMedia, { 
  MediaParticipant, 
  BACKGROUND_BLUR_LEVELS, 
  VIRTUAL_BACKGROUND_COLORS,
  VIDEO_QUALITY_PRESETS,
  BackgroundBlurLevel,
  VideoQuality,
} from '../../hooks/useAgoraMedia';
import { isMobileDevice } from '../../utils/platform';
import { isIOSVoiceVideoBlocked } from '../../utils/iosUtils';
import { useBlockedUsers, blockUser, unblockUser } from '../../services/blockService';
import { submitReport, REPORT_REASONS, ReportReason } from '../../services/reportService';
import { captureScreenshot, uploadEvidence } from '../../services/reportEvidenceService';
import { formatSeconds, formatResetDay, unlockTableVoice } from '../../services/voiceAccessService';
import { voicePassService } from '../../services/voicePassService';
import { Mic, MicOff, Video, VideoOff, Volume2, VolumeX, Headphones, PhoneOff, Sparkles, ChevronDown, AlertTriangle, Info, User, Check, SwitchCamera, MoreHorizontal, Ban, Flag, Clock, LayoutGrid } from 'lucide-react';
import '../../styles/MediaChatBar.css';

interface MediaChatBarProps {
  tableId: string;
  odid: string;  // Current user's Firebase UID
  mediaChatEnabled: boolean;
  usernames?: { [odid: string]: string };  // Map of Firebase UID -> display name
  /** Tournament table: the mic follows the seat across table moves. */
  isTournament?: boolean;
  /** 'voice' hides the camera controls (tournament voice-only tables). */
  mediaMode?: 'voice' | 'video';
  /** A recording is running and this player declined: camera and mic stay off. */
  mediaBlocked?: boolean;
  /** Offer "Join + Record" next to Join. Starts a paid (or free for partners) recording after joining. */
  onJoinAndRecord?: (showCards: boolean) => Promise<any>;
  recordingOffer?: { label: string; enabled: boolean } | null;
  /** Headless recorder view: auto-join receive-only with the video grid open. */
  recorderMode?: boolean;
}

// ============================================================================
//  MOBILE VIDEO THUMBNAIL - Compact video with username + per-user controls

// ============================================================================
const MobileVideoThumbnail: React.FC<{ 
  participant: MediaParticipant; 
  username: string;
  isLocal?: boolean;
  isMuted?: boolean;
  isVideoOn?: boolean;
  videoTrack?: any;
  isSpeaking: boolean;
  onMuteUser?: (uid: number, mute: boolean) => void;
  onHideVideo?: (uid: number, hide: boolean) => void;
  onOpenActions?: (participant: any) => void;
  isNativePlatform?: boolean;
  setupNativeVideoView?: (options: { uid?: number; element: HTMLElement; isLocal: boolean }) => Promise<void>;
  removeNativeVideoView?: (viewId: string) => Promise<void>;
}> = ({ participant, username, isLocal, isMuted, isVideoOn, videoTrack, isSpeaking, onMuteUser, onHideVideo, onOpenActions, isNativePlatform, setupNativeVideoView, removeNativeVideoView }) => {
  const videoRef = useRef<HTMLDivElement>(null);
  const [showControls, setShowControls] = useState(false);

  // Web: play track into DOM element
  useEffect(() => {
    if (isNativePlatform) return; // Skip web track play on native
    const track = isLocal ? videoTrack : participant?.videoTrack;
    if (track && videoRef.current) {
      track.play(videoRef.current);
    }
    return () => {
      if (!isLocal && participant?.videoTrack && !isNativePlatform) {
        participant.videoTrack.stop();
      }
    };
  }, [isLocal ? videoTrack : participant?.videoTrack, isNativePlatform]);

  // Native: position the native video view over the thumbnail rect. When
  // the camera goes off (or this thumb unmounts) the native UIView must be
  // torn down too, or it lingers as a black rectangle over the web UI.
  useEffect(() => {
    if (!isNativePlatform) return;
    const viewId = isLocal ? 'local' : `remote-${participant?.uid}`;
    const hasVideo = isLocal ? isVideoOn : participant?.hasVideo;
    if (hasVideo && videoRef.current && setupNativeVideoView) {
      setupNativeVideoView({
        uid: isLocal ? undefined : Number(participant?.uid),
        element: videoRef.current,
        isLocal: !!isLocal,
      });
    } else {
      removeNativeVideoView?.(viewId);
    }
    return () => { removeNativeVideoView?.(viewId); };
  }, [isNativePlatform, setupNativeVideoView, removeNativeVideoView, isLocal, isVideoOn, participant?.hasVideo, participant?.uid]);

  // Native has no web videoTrack object - gating on it kept the video
  // container from ever mounting in the iOS app, so the native view had
  // nowhere to attach. On native, isVideoOn/hasVideo alone decide.
  const hasVideo = isLocal
    ? (isVideoOn && (isNativePlatform || videoTrack))
    : participant?.hasVideo && !participant?.isVideoHiddenByMe;
  const showSpeaking = isLocal ? (isSpeaking && !isMuted) : (participant?.isSpeaking && !participant?.isMutedByMe);
  
  // Get first letter of username for avatar
  const avatarLetter = username.charAt(0).toUpperCase();

  // Handle tap to toggle controls (mobile)
  const handleTap = () => {
    if (!isLocal && participant) {
      setShowControls(!showControls);
    }
  };

  // Auto-hide controls after 3 seconds
  useEffect(() => {
    if (showControls) {
      const timer = setTimeout(() => setShowControls(false), 3000);
      return () => clearTimeout(timer);
    }
  }, [showControls]);

  return (
    <div 
      className={`mobile-video-thumb ${showSpeaking ? 'speaking' : ''} ${isLocal ? 'local' : ''} ${showControls ? 'show-controls' : ''}`}
      onClick={handleTap}
    >
      {hasVideo ? (
        <div ref={videoRef} className="thumb-video-container" />
      ) : (
        <div className="thumb-avatar">
          <span className="thumb-avatar-letter">{avatarLetter}</span>
        </div>
      )}
      
      {/* Speaking indicator ring */}
      {showSpeaking && <div className="thumb-speaking-ring" />}
      
      {/* 🆕 Per-user control overlay - shows on tap (mobile) */}
      {!isLocal && showControls && participant && (
        <div className="thumb-user-controls">
          <button
            className={`thumb-ctrl-btn ${participant.isMutedByMe ? 'active' : ''}`}
            onClick={(e) => {
              e.stopPropagation();
              onMuteUser?.(participant.uid, !participant.isMutedByMe);
            }}
          >
            {participant.isMutedByMe ? <MicOff size={16} /> : <Mic size={16} />}

          </button>
          <button
            className={`thumb-ctrl-btn ${participant.isVideoHiddenByMe ? 'active' : ''}`}
            onClick={(e) => {
              e.stopPropagation();
              onHideVideo?.(participant.uid, !participant.isVideoHiddenByMe);
            }}
          >
            {participant.isVideoHiddenByMe ? <Video size={16} /> : <VideoOff size={16} />}

          </button>
          <button
            className="thumb-ctrl-btn"
            onClick={(e) => {
              e.stopPropagation();
              onOpenActions?.(participant);
            }}
            title="Block or report"
          >
            <MoreHorizontal size={16} />
          </button>
        </div>
      )}
      
      {/* Username label - ALWAYS visible */}
      <div className="thumb-name-label">
        <span className="thumb-name">{isLocal ? 'You' : username}</span>
        {(isLocal ? isMuted : !participant?.hasAudio || participant?.isMutedByMe) && (
          <span className="thumb-muted"><MicOff size={12} /></span>

        )}
        {!isLocal && participant?.isVideoHiddenByMe && (
          <span className="thumb-muted"><VideoOff size={12} /></span>

        )}
      </div>
    </div>
  );
};

// ============================================================================
//  DESKTOP Video Player Component for remote users - WITH PER-USER CONTROLS

// ============================================================================
const RemoteVideoPlayer: React.FC<{ 
  participant: MediaParticipant; 
  username: string;
  onMuteUser: (uid: number, mute: boolean) => void;
  onHideVideo: (uid: number, hide: boolean) => void;
  onOpenActions?: (participant: any) => void;
  isNativePlatform?: boolean;
  setupNativeVideoView?: (options: { uid?: number; element: HTMLElement; isLocal: boolean }) => Promise<void>;
}> = ({ participant, username, onMuteUser, onHideVideo, onOpenActions, isNativePlatform, setupNativeVideoView }) => {
  const videoRef = useRef<HTMLDivElement>(null);
  const [showControls, setShowControls] = useState(false);

  // Web: play track
  useEffect(() => {
    if (isNativePlatform) return;
    if (participant.videoTrack && videoRef.current) {
      participant.videoTrack.play(videoRef.current);
    }
    return () => {
      if (!isNativePlatform) participant.videoTrack?.stop();
    };
  }, [participant.videoTrack, isNativePlatform]);

  // Native: position native video view
  useEffect(() => {
    if (!isNativePlatform || !setupNativeVideoView || !videoRef.current || !participant.hasVideo) return;
    setupNativeVideoView({ uid: Number(participant.uid), element: videoRef.current, isLocal: false });
  }, [isNativePlatform, setupNativeVideoView, participant.hasVideo, participant.uid]);

  return (
    <div 
      className={`video-participant ${participant.isSpeaking ? 'speaking' : ''} ${participant.isMutedByMe ? 'muted-by-me' : ''}`}
      onMouseEnter={() => setShowControls(true)}
      onMouseLeave={() => setShowControls(false)}
    >
      {participant.hasVideo && !participant.isVideoHiddenByMe ? (
        <div ref={videoRef} className="video-container" />
      ) : (
        <div className="video-placeholder">
          <span className="avatar-placeholder"><User size={30} /></span>

        </div>
      )}
      
      {/* Speaking badge */}
      {participant.isSpeaking && !participant.isMutedByMe && (
        <div className="speaking-badge"><Volume2 size={12} /></div>

      )}
      
      {/* Volume bar */}
      {participant.isSpeaking && (
        <div className="volume-bar" style={{ width: `${Math.min(participant.volume || 0, 100)}%` }} />
      )}
      
      {/* 🆕 Per-user control overlay - shows on hover */}
      {showControls && (
        <div className="video-user-controls">
          <button
            className={`user-ctrl-btn ${participant.isMutedByMe ? 'active' : ''}`}
            onClick={(e) => {
              e.stopPropagation();
              onMuteUser(participant.uid, !participant.isMutedByMe);
            }}
            title={participant.isMutedByMe ? 'Unmute this person' : 'Mute this person'}
          >
            {participant.isMutedByMe ? <MicOff size={16} /> : <Mic size={16} />}

          </button>
          <button
            className={`user-ctrl-btn ${participant.isVideoHiddenByMe ? 'active' : ''}`}
            onClick={(e) => {
              e.stopPropagation();
              onHideVideo(participant.uid, !participant.isVideoHiddenByMe);
            }}
            title={participant.isVideoHiddenByMe ? 'Show video' : 'Hide video'}
          >
            {participant.isVideoHiddenByMe ? <Video size={16} /> : <VideoOff size={16} />}

          </button>
          <button
            className="user-ctrl-btn"
            onClick={(e) => {
              e.stopPropagation();
              onOpenActions?.(participant);
            }}
            title="Block or report"
          >
            <MoreHorizontal size={16} />
          </button>
        </div>
      )}
      
      <div className="video-label">
        <span className={`video-name ${participant.isSpeaking ? 'speaking' : ''}`}>{username}</span>
        {!participant.hasAudio && <span className="muted-icon"><MicOff size={12} /></span>}

        {participant.isMutedByMe && <span className="muted-icon"><VolumeX size={12} /></span>}

        {participant.isVideoHiddenByMe && <span className="muted-icon"><VideoOff size={12} /></span>}

      </div>
    </div>
  );
};

// ============================================================================
//  DESKTOP Local Video Player Component (unchanged)

// ============================================================================
const LocalVideoPlayer: React.FC<{ 
  videoTrack: any; 
  isMuted: boolean;
  isVideoOn: boolean;
  isSpeaking: boolean;
  username: string;
  isNativePlatform?: boolean;
  setupNativeVideoView?: (options: { uid?: number; element: HTMLElement; isLocal: boolean }) => Promise<void>;
}> = ({ videoTrack, isMuted, isVideoOn, isSpeaking, username, isNativePlatform, setupNativeVideoView }) => {
  const videoRef = useRef<HTMLDivElement>(null);

  // Web: play track into DOM
  useEffect(() => {
    if (isNativePlatform) return;
    if (videoTrack && videoRef.current) {
      videoTrack.play(videoRef.current);
    }
  }, [videoTrack, isNativePlatform]);

  // Native: position native video view
  useEffect(() => {
    if (!isNativePlatform || !setupNativeVideoView || !videoRef.current || !isVideoOn) return;
    setupNativeVideoView({ element: videoRef.current, isLocal: true });
  }, [isNativePlatform, setupNativeVideoView, isVideoOn]);

  return (
    <div className={`video-participant local ${isSpeaking && !isMuted ? 'speaking' : ''}`}>
      {isVideoOn && videoTrack ? (
        <div ref={videoRef} className="video-container" />
      ) : (
        <div className="video-placeholder">
          <span className="avatar-placeholder"><User size={30} /></span>

        </div>
      )}
      
      {/* Speaking badge for local user */}
      {isSpeaking && !isMuted && (
        <div className="speaking-badge"><Volume2 size={12} /></div>

      )}
      
      <div className="video-label">
        <span className={`video-name ${isSpeaking && !isMuted ? 'speaking' : ''}`}>You</span>
        {isMuted && <span className="muted-icon"><MicOff size={12} /></span>}

        {!isVideoOn && <span className="video-off-icon"><VideoOff size={12} /></span>}

      </div>
    </div>
  );
};

// ============================================================================
// 🆕 Background Settings Panel Component (unchanged from original)
// ============================================================================
const BackgroundSettingsPanel: React.FC<{
  isVideoOn: boolean;
  backgroundSettings: { type: string; blurLevel?: number; color?: string; imageUrl?: string };
  isBackgroundSupported: boolean;
  isBackgroundLoading: boolean;
  videoQuality: VideoQuality;
  deviceCapabilities: { isLowEnd: boolean; isMobile: boolean; recommendedQuality: string };
  onSetBlur: (level: BackgroundBlurLevel) => void;
  onSetColor: (color: string) => void;
  onSetImage: (url: string) => void;
  onClear: () => void;
  onSetQuality: (quality: VideoQuality) => void;
  onClose: () => void;
}> = ({
  isVideoOn,
  backgroundSettings,
  isBackgroundSupported,
  isBackgroundLoading,
  videoQuality,
  deviceCapabilities,
  onSetBlur,
  onSetColor,
  onSetImage,
  onClear,
  onSetQuality,
  onClose,
}) => {
  const [customImageUrl, setCustomImageUrl] = useState('');
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (event) => {
        const dataUrl = event.target?.result as string;
        onSetImage(dataUrl);
      };
      reader.readAsDataURL(file);
    }
  };

  if (!isBackgroundSupported) {
    return (
      <div className="background-settings-panel">
        <div className="bg-panel-header">
          <span><Sparkles size={14} style={{ verticalAlign: -2, marginRight: 6 }} />Background</span>

          <button className="bg-close-btn" onClick={onClose} title="Close">×</button>

        </div>
        <div className="bg-not-supported">
          <span></span>

          <p>Virtual backgrounds are not supported on this device/browser.</p>
        </div>
      </div>
    );
  }

  if (!isVideoOn) {
    return (
      <div className="background-settings-panel">
        <div className="bg-panel-header">
          <span><Sparkles size={14} style={{ verticalAlign: -2, marginRight: 6 }} />Background</span>

          <button className="bg-close-btn" onClick={onClose} title="Close">×</button>

        </div>
        <div className="bg-not-supported">
          <span></span>

          <p>Turn on your camera to use background effects.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="background-settings-panel">
      <div className="bg-panel-header">
        <span><Sparkles size={14} style={{ verticalAlign: -2, marginRight: 6 }} />Background Effects</span>

        <button className="bg-close-btn" onClick={onClose} title="Close">×</button>

      </div>

      {isBackgroundLoading && (
        <div className="bg-loading">
          <div className="bg-spinner" />
          <span>Applying effect...</span>
        </div>
      )}

      {/* Video Quality Section */}
      <div className="bg-section">
        <h4> Video Quality</h4>

        <div className="bg-quality-options">
          {(Object.keys(VIDEO_QUALITY_PRESETS) as VideoQuality[]).map((quality) => (
            <button
              key={quality}
              className={`bg-quality-btn ${videoQuality === quality ? 'active' : ''} ${deviceCapabilities.recommendedQuality === quality ? 'recommended' : ''}`}
              onClick={() => onSetQuality(quality)}
              disabled={isBackgroundLoading}
            >
              <span className="quality-label">{quality.charAt(0).toUpperCase() + quality.slice(1)}</span>
              {deviceCapabilities.recommendedQuality === quality && (
                <span className="quality-rec"></span>

              )}
            </button>
          ))}
        </div>
        {deviceCapabilities.isLowEnd && (
          <div className="quality-hint"> Low quality recommended for your device</div>

        )}
        {backgroundSettings.type !== 'none' && (
          <div className="quality-hint"> Quality auto-reduced while background effect is active</div>

        )}
      </div>

      {/* Blur Section */}
      <div className="bg-section">
        <h4> Background Blur</h4>

        <div className="bg-blur-options">
          <button
            className={`bg-blur-btn ${backgroundSettings.type === 'none' ? 'active' : ''}`}
            onClick={onClear}
            disabled={isBackgroundLoading}
          >
            <span className="blur-icon">-</span>

            <span>Off</span>
          </button>
          <button
            className={`bg-blur-btn ${backgroundSettings.type === 'blur' && backgroundSettings.blurLevel === BACKGROUND_BLUR_LEVELS.LOW ? 'active' : ''}`}
            onClick={() => onSetBlur(BACKGROUND_BLUR_LEVELS.LOW)}
            disabled={isBackgroundLoading}
          >
            <span className="blur-icon blur-low">○</span>
            <span>Low</span>
          </button>
          <button
            className={`bg-blur-btn ${backgroundSettings.type === 'blur' && backgroundSettings.blurLevel === BACKGROUND_BLUR_LEVELS.MEDIUM ? 'active' : ''}`}
            onClick={() => onSetBlur(BACKGROUND_BLUR_LEVELS.MEDIUM)}
            disabled={isBackgroundLoading}
          >
            <span className="blur-icon blur-med">◐</span>
            <span>Medium</span>
          </button>
          <button
            className={`bg-blur-btn ${backgroundSettings.type === 'blur' && backgroundSettings.blurLevel === BACKGROUND_BLUR_LEVELS.HIGH ? 'active' : ''}`}
            onClick={() => onSetBlur(BACKGROUND_BLUR_LEVELS.HIGH)}
            disabled={isBackgroundLoading}
          >
            <span className="blur-icon blur-high">●</span>
            <span>High</span>
          </button>
        </div>
      </div>

      {/* Color Section */}
      <div className="bg-section">
        <h4> Solid Colors</h4>

        <div className="bg-color-grid">
          {VIRTUAL_BACKGROUND_COLORS.map((bg) => (
            <button
              key={bg.color}
              className={`bg-color-btn ${backgroundSettings.type === 'color' && backgroundSettings.color === bg.color ? 'active' : ''}`}
              style={{ backgroundColor: bg.color }}
              onClick={() => onSetColor(bg.color)}
              disabled={isBackgroundLoading}
              title={bg.name}
            >
              {backgroundSettings.type === 'color' && backgroundSettings.color === bg.color && (
                <span className="color-check"><Check size={12} /></span>

              )}
            </button>
          ))}
        </div>
      </div>

      {/* Image Section */}
      <div className="bg-section">
        <h4> Custom Image</h4>

        <div className="bg-image-options">
          <button
            className="bg-upload-btn"
            onClick={() => fileInputRef.current?.click()}
            disabled={isBackgroundLoading}
          >
             Upload Image

          </button>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            onChange={handleFileUpload}
            style={{ display: 'none' }}
          />
        </div>
        
        {/* URL input */}
        <div className="bg-url-input">
          <input
            type="text"
            placeholder="Or paste image URL..."
            value={customImageUrl}
            onChange={(e) => setCustomImageUrl(e.target.value)}
            disabled={isBackgroundLoading}
          />
          <button
            onClick={() => {
              if (customImageUrl.trim()) {
                onSetImage(customImageUrl.trim());
                setCustomImageUrl('');
              }
            }}
            disabled={isBackgroundLoading || !customImageUrl.trim()}
          >
            Apply
          </button>
        </div>

        {backgroundSettings.type === 'image' && (
          <div className="bg-current-image">
            <span> Custom image applied</span>

          </div>
        )}
      </div>

      {/* Current Effect Display */}
      {backgroundSettings.type !== 'none' && (
        <div className="bg-current-effect">
          <span>
            Current: {backgroundSettings.type === 'blur' ? `Blur (${['Off', 'Low', 'Medium', 'High'][backgroundSettings.blurLevel || 0]})` :
                     backgroundSettings.type === 'color' ? `Color (${backgroundSettings.color})` :
                     backgroundSettings.type === 'image' ? 'Custom Image' : 'None'}
          </span>
          <button className="bg-clear-btn" onClick={onClear} disabled={isBackgroundLoading}>
            Clear Effect
          </button>
        </div>
      )}
    </div>
  );
};

// ============================================================================
//  MAIN MEDIA CHAT BAR COMPONENT

// ============================================================================
const MediaChatBar: React.FC<MediaChatBarProps> = ({
  tableId,
  odid,
  mediaChatEnabled,
  usernames = {},
  isTournament = false,
  mediaMode = 'video',
  mediaBlocked = false,
  onJoinAndRecord,
  recordingOffer = null,
  recorderMode = false,
}) => {
  const [recNotice, setRecNotice] = useState<string | null>(null);
  const [recShowCards, setRecShowCards] = useState(true);
  const [showJoinPrompt, setShowJoinPrompt] = useState(true);
  const [isExpanded, setIsExpanded] = useState(false);
  const [showVideoGrid, setShowVideoGrid] = useState(false);
  const [showBackgroundSettings, setShowBackgroundSettings] = useState(false);
  
  // 🆕 MOBILE: Minimized state for mobile controls
  const [isMinimized, setIsMinimized] = useState(false);
  
  // 🆕 Detect mobile device
  const isMobile = isMobileDevice();

  const {
    isConnected,
    isConnecting,
    isMuted,
    isVideoOn,
    isDeafened,
    participants,
    voiceAccess,
    voiceAccessAt,
    refreshVoiceAccess,
    localVideoTrack,
    isLocalSpeaking,
    error,
    // Background state
    backgroundSettings,
    isBackgroundSupported,
    isBackgroundLoading,
    // Performance state
    videoQuality,
    deviceCapabilities,
    // Actions
    joinChannel,
    leaveChannel,
    toggleMute,
    toggleVideo,
    toggleDeafen,
    // Background actions
    setBackgroundBlur,
    setBackgroundColor,
    setBackgroundImage,
    clearBackground,
    // Performance actions
    setVideoQuality,
    // 🆕 Per-user controls
    muteRemoteUser,
    hideRemoteUserVideo,
    //  NATIVE: Video view management

    isNativePlatform,
    setupNativeVideoView,
    removeNativeVideoView,
    switchCamera,
  } = useAgoraMedia(tableId);

  // Recorder: join receive-only as soon as the bar mounts, open the video grid
  const recorderJoinedRef = useRef(false);
  useEffect(() => {
    if (!recorderMode || recorderJoinedRef.current || isConnected || isConnecting) return;
    recorderJoinedRef.current = true;
    setShowJoinPrompt(false);
    setIsExpanded(true);
    setShowVideoGrid(true);
    setIsMinimized(false);
    joinChannel(undefined, 'Recorder', { subscribeOnly: true }).catch((e) => console.warn('[Recorder] join failed', e));
  }, [recorderMode, isConnected, isConnecting, joinChannel]);

  // Declined a recording: leave the channel and stay out until it ends
  useEffect(() => {
    if (mediaBlocked && isConnected) leaveChannel().catch(() => {});
  }, [mediaBlocked, isConnected, leaveChannel]);

  // Get current user's username from the usernames prop
  const currentUsername = usernames[odid] || `Player ${odid.substring(0, 8)}`;

  // ── Free minutes: countdown, warnings, paywall ──────────────────────────
  const [showPaywall, setShowPaywall] = useState(false);
  const [paywallBusy, setPaywallBusy] = useState<'pass' | 'table' | null>(null);
  const [paywallNotice, setPaywallNotice] = useState<string | null>(null);
  const [voiceNotice, setVoiceNotice] = useState<string | null>(null);

  // 🎙 The mic follows the seat: when a tournament move lands us on a new
  // table and we were connected on the old one (useAgoraMedia stamps
  // pss:mediaCarry on the way out), rejoin here without asking. Mute and
  // camera state are restored by the hook from the same stamp.
  const carryTriedRef = useRef(false);
  const clearCarry = () => { try { sessionStorage.removeItem('pss:mediaCarry'); } catch { /* noop */ } };
  useEffect(() => {
    if (!tableId || carryTriedRef.current || recorderMode) return;
    // A stamp only ever means "rejoin on the NEXT tournament table". On a
    // cash table, or on a reload of the same table, it is stale: drop it.
    if (!isTournament) { clearCarry(); return; }
    let carry: any = null;
    try { carry = JSON.parse(sessionStorage.getItem('pss:mediaCarry') || 'null'); } catch { carry = null; }
    if (!carry || !carry.connected) return;
    if (carry.fromTableId === tableId || Date.now() - (carry.at || 0) > 3 * 60 * 1000) { clearCarry(); return; }
    if (voiceAccess === null) return; // allowance not known yet; effect re-runs when it lands
    carryTriedRef.current = true;
    if (voiceAccess?.mode === 'exhausted') { clearCarry(); return; }
    setShowJoinPrompt(false);
    if (!mediaBlocked) joinChannel(undefined, currentUsername).catch(() => setShowJoinPrompt(true));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isTournament, tableId, voiceAccess]);
  // If the silent rejoin did not end in a connection (permission denied,
  // token error, blocked), give the player their Join button back.
  useEffect(() => {
    if (!carryTriedRef.current || isConnected || isConnecting) return;
    const t = setTimeout(() => { if (!isConnected && !isConnecting) { setShowJoinPrompt(true); clearCarry(); } }, error ? 0 : 25_000);
    return () => clearTimeout(t);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isConnected, isConnecting, error]);
  const [nowTick, setNowTick] = useState(Date.now());
  const warnedRef = useRef<{ ten: boolean; two: boolean }>({ ten: false, two: false });

  // Know the allowance before the first tap so the join button can say it.
  useEffect(() => { refreshVoiceAccess(); }, [refreshVoiceAccess]);

  // Tick once a second while metered so the pill counts down between beats.
  useEffect(() => {
    if (!isConnected || voiceAccess?.mode !== 'free') return;
    const id = setInterval(() => setNowTick(Date.now()), 1000);
    return () => clearInterval(id);
  }, [isConnected, voiceAccess?.mode]);

  const freeRemaining = voiceAccess
    ? (isConnected && voiceAccess.mode === 'free'
        ? Math.max(0, voiceAccess.remainingSeconds - Math.floor((nowTick - voiceAccessAt) / 1000))
        : voiceAccess.remainingSeconds)
    : null;

  useEffect(() => {
    if (!isConnected || voiceAccess?.mode !== 'free' || freeRemaining === null) { warnedRef.current = { ten: false, two: false }; return; }
    if (freeRemaining <= 600 && !warnedRef.current.ten) {
      warnedRef.current.ten = true;
      setVoiceNotice('10 minutes of free voice left this week');
      setTimeout(() => setVoiceNotice(null), 6000);
    }
    if (freeRemaining <= 120 && !warnedRef.current.two) {
      warnedRef.current.two = true;
      setVoiceNotice('2 minutes left. Grab a pass to keep talking.');
      setTimeout(() => setVoiceNotice(null), 8000);
    }
  }, [freeRemaining, isConnected, voiceAccess?.mode]);

  // Out of minutes (at join, or cut off mid-session) opens the paywall.
  useEffect(() => {
    if (error === 'free_minutes_exhausted') setShowPaywall(true);
  }, [error]);

  const handleBuyPass = async () => {
    setPaywallBusy('pass');
    setPaywallNotice(null);
    const r = await voicePassService.purchaseUnlimitedPass(odid);
    setPaywallBusy(null);
    if (!r.success) { setPaywallNotice(r.error || 'Purchase failed'); return; }
    setShowPaywall(false);
    await refreshVoiceAccess();
    setShowJoinPrompt(false);
    await joinChannel(undefined, currentUsername);
  };
  const handleUnlockTable = async () => {
    setPaywallBusy('table');
    setPaywallNotice(null);
    const r = await unlockTableVoice(tableId);
    setPaywallBusy(null);
    if (!r.success) { setPaywallNotice(r.error || 'Could not unlock this table'); return; }
    setShowPaywall(false);
    await refreshVoiceAccess();
    setShowJoinPrompt(false);
    await joinChannel(undefined, currentUsername);
  };

  const accessLabel = (() => {
    if (!voiceAccess) return null;
    if (voiceAccess.mode === 'table_unlimited') return 'Unlimited at this table';
    if (voiceAccess.mode === 'pass') return 'Unlimited pass';
    if (voiceAccess.mode === 'free') return `${formatSeconds(freeRemaining ?? voiceAccess.remainingSeconds)} free`;
    return 'Free minutes used';
  })();

  // ── Block / report a participant ─────────────────────────────────────────
  const blockedUsers = useBlockedUsers(odid);
  const [actionTarget, setActionTarget] = useState<MediaParticipant | null>(null);
  const [actionMode, setActionMode] = useState<'menu' | 'report'>('menu');
  const [reportReason, setReportReason] = useState<ReportReason | ''>('');
  const [reportDetails, setReportDetails] = useState('');
  const [actionBusy, setActionBusy] = useState(false);
  const [actionNotice, setActionNotice] = useState<string | null>(null);
  const [evidenceBlob, setEvidenceBlob] = useState<Blob | null>(null);
  const [evidencePreview, setEvidencePreview] = useState<string | null>(null);
  const clearEvidence = useCallback(() => {
    setEvidencePreview((prev) => { if (prev) URL.revokeObjectURL(prev); return null; });
    setEvidenceBlob(null);
  }, []);

  const openParticipantActions = useCallback((p: MediaParticipant) => {
    setActionTarget(p);
    setActionMode('menu');
    setReportReason('');
    setReportDetails('');
    setActionNotice(null);
    // Capture the table as it looks at this moment, before the sheet opens,
    // so a report carries a picture of what the player actually saw.
    clearEvidence();
    captureScreenshot().then((blob) => {
      if (!blob) return;
      setEvidenceBlob(blob);
      setEvidencePreview(URL.createObjectURL(blob));
    });
  }, [clearEvidence]);
  const closeParticipantActions = useCallback(() => { setActionTarget(null); clearEvidence(); }, [clearEvidence]);

  // A blocked player is silent and invisible to you at every table. Apply it
  // whenever they show up; the hook clears its own mute list on leave/rejoin.
  useEffect(() => {
    participants.forEach((p) => {
      if (!p.odid || !blockedUsers.has(p.odid)) return;
      if (!p.isMutedByMe) muteRemoteUser(p.uid, true);
      if (!p.isVideoHiddenByMe) hideRemoteUserVideo(p.uid, true);
    });
  }, [participants, blockedUsers, muteRemoteUser, hideRemoteUserVideo]);

  const handleBlockToggle = async () => {
    if (!actionTarget?.odid) {
      setActionNotice('This player cannot be blocked right now.');
      return;
    }
    setActionBusy(true);
    const wasBlocked = blockedUsers.has(actionTarget.odid);
    const r = wasBlocked ? await unblockUser(actionTarget.odid) : await blockUser(actionTarget.odid);
    setActionBusy(false);
    if (!r.success) {
      setActionNotice(r.error || 'Something went wrong.');
      return;
    }
    if (wasBlocked) {
      muteRemoteUser(actionTarget.uid, false);
      hideRemoteUserVideo(actionTarget.uid, false);
    }
    setActionNotice(wasBlocked ? 'Unblocked.' : 'Blocked. They are muted and hidden for you everywhere.');
    setTimeout(closeParticipantActions, 1200);
  };

  const handleSubmitReport = async () => {
    if (!actionTarget?.odid || !reportReason) return;
    setActionBusy(true);
    let evidenceUrls: string[] = [];
    if (evidenceBlob) {
      try { evidenceUrls = [await uploadEvidence(odid, evidenceBlob)]; }
      catch (e) { console.warn('[evidence] upload failed, sending report without it', e); }
    }
    const r = await submitReport(
      actionTarget.odid,
      reportReason,
      `Voice/video at table ${tableId}. ${reportDetails.trim()}`.trim(),
      evidenceUrls,
    );
    setActionBusy(false);
    setActionNotice(r.success ? 'Report sent. Thank you.' : (r.message || 'Could not send report.'));
    if (r.success) setTimeout(closeParticipantActions, 1400);
  };

  // Reset join prompt when disconnected
  useEffect(() => {
    if (!isConnected) {
      setShowJoinPrompt(!recorderMode);
      setShowVideoGrid(false);
      setShowBackgroundSettings(false);
      setIsMinimized(false);
    }
  }, [isConnected, recorderMode]);

  // Recorder: once connected, keep the video grid open and the bar expanded
  // so every camera tile is in the recording.
  useEffect(() => {
    if (!recorderMode || !isConnected) return;
    setShowVideoGrid(true);
    setIsExpanded(true);
    setIsMinimized(false);
    setShowBackgroundSettings(false);
  }, [recorderMode, isConnected]);

  // Close background settings when video is turned off
  useEffect(() => {
    if (!isVideoOn) {
      setShowBackgroundSettings(false);
    }
  }, [isVideoOn]);

  // Don't render if media chat is not enabled for this table
  if (!mediaChatEnabled) {
    return null;
  }

  //  iOS native: Voice & Video requires web browser

  if (isIOSVoiceVideoBlocked()) {
    return (
      <div className="media-chat-bar" style={{ padding: '8px 12px' }}>
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          padding: '8px 12px',
          background: 'rgba(245, 158, 11, 0.1)',
          border: '1px solid rgba(245, 158, 11, 0.3)',
          borderRadius: '8px',
          fontSize: '13px',
          color: '#fbbf24',
        }}>
          <span><Info size={16} /></span>

          <span>Voice & Video available on Web browsers. Open in Safari or Chrome for full access.</span>
        </div>
      </div>
    );
  }

  // Total participants including self
  const totalParticipants = isConnected ? participants.length + 1 : 0;

  // Handle join click
  const handleJoin = async () => {
    if (voiceAccess?.mode === 'exhausted') {
      setShowPaywall(true);
      return;
    }
    setShowJoinPrompt(false);
    await joinChannel(undefined, currentUsername);
  };

  // Handle leave click
  const handleLeave = async () => {
    await leaveChannel();
    setShowJoinPrompt(true);
    setShowVideoGrid(false);
    setShowBackgroundSettings(false);
    setIsMinimized(false);
  };


  // Toggle video grid visibility
  const handleToggleVideoGrid = () => {
    setShowVideoGrid(!showVideoGrid);
    if (!showVideoGrid) {
      setShowBackgroundSettings(false);
    }
  };

  // Toggle background settings panel
  const handleToggleBackgroundSettings = () => {
    setShowBackgroundSettings(!showBackgroundSettings);
    if (!showBackgroundSettings) {
      setShowVideoGrid(false);
    }
  };

  // 🆕 Toggle minimized state (mobile)
  const handleToggleMinimize = () => {
    setIsMinimized(!isMinimized);
    if (!isMinimized) {
      // When minimizing, close panels
      setShowBackgroundSettings(false);
      setIsExpanded(false);
    }
  };

  // Get display name for a remote participant
  const getDisplayName = (participant: MediaParticipant): string => {
    if (participant.username && participant.username !== `Player ${participant.uid}`) {
      return participant.username;
    }
    if (participant.odid && usernames[participant.odid]) {
      return usernames[participant.odid];
    }
    if (usernames[String(participant.uid)]) {
      return usernames[String(participant.uid)];
    }
    return participant.username || `Player ${participant.uid}`;
  };

  // Check if any background effect is active
  const hasActiveBackground = backgroundSettings.type !== 'none';
  
  // Check if anyone is speaking (for minimized indicator)
  const anyoneSpeaking = isLocalSpeaking || participants.some(p => p.isSpeaking);

  // ============================================================================
  //  MOBILE RENDER

  // ============================================================================
  if (isMobile) {
    return (
      <>
        {/* 🆕 TOP VIDEO STRIP - Always visible when connected (unless minimized) */}
        {isConnected && !isMinimized && (
          <div className="mobile-video-strip">
            {/* Minimize button */}
            <button 
              className="strip-minimize-btn"
              onClick={handleToggleMinimize}
              title="Minimize videos"
            >
              ▼
            </button>
            
            {/* Video thumbnails - horizontal scroll */}
            <div className="strip-videos">
              {/* Local user first */}
              <MobileVideoThumbnail
                participant={null as any}
                username={currentUsername}
                isLocal={true}
                isMuted={isMuted}
                isVideoOn={isVideoOn}
                videoTrack={localVideoTrack}
                isSpeaking={isLocalSpeaking}
                isNativePlatform={isNativePlatform}
                setupNativeVideoView={setupNativeVideoView}
                removeNativeVideoView={removeNativeVideoView}
              />

              {/* Remote participants */}
              {participants.map((p) => (
                <MobileVideoThumbnail
                  key={p.uid}
                  participant={p}
                  username={getDisplayName(p)}
                  isSpeaking={p.isSpeaking}
                  onMuteUser={muteRemoteUser}
                  onHideVideo={hideRemoteUserVideo}
                  onOpenActions={openParticipantActions}
                  isNativePlatform={isNativePlatform}
                  setupNativeVideoView={setupNativeVideoView}
                  removeNativeVideoView={removeNativeVideoView}
                />
              ))}
            </div>
            
            {/* Participant count badge */}
            <div className="strip-count-badge">
              {totalParticipants}
            </div>
          </div>
        )}

        {/* 🆕 RIGHT SIDE CONTROLS - Pill that expands */}
        <div className={`mobile-media-controls ${isConnected ? 'connected' : ''} ${isMinimized ? 'minimized' : ''}`}>
          
          {/* Join Button - Shows when not connected */}
          {!isConnected && showJoinPrompt && (
            <button
              className="mobile-join-btn"
              onClick={handleJoin}
              disabled={isConnecting}
              title="Join Voice/Video"
            >
              {isConnecting ? (
                <span className="mobile-spinner" />
              ) : (
                <Mic size={22} />

              )}
            </button>
          )}
          {!isConnected && showJoinPrompt && onJoinAndRecord && recordingOffer?.enabled && (
            <button
              className="mobile-join-btn"
              style={{ background: 'rgba(239,68,68,.25)', border: '1px solid rgba(239,68,68,.7)', marginLeft: 6 }}
              disabled={isConnecting}
              title={`Join and record (${recordingOffer.label})`}
              onClick={async () => {
                setRecNotice(null);
                await handleJoin();
                try { await onJoinAndRecord(recShowCards); } catch (e: any) { setRecNotice(e?.message || 'Could not start recording'); }
              }}
            >
              <span style={{ color: '#f87171', fontSize: 18, lineHeight: 1 }}>●</span>
            </button>
          )}
          {!isConnected && showJoinPrompt && onJoinAndRecord && recordingOffer?.enabled && (
            <label
              title="Show my cards in the recording"
              onClick={(e) => e.stopPropagation()}
              style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 11, color: '#cbd5e1', marginLeft: 6, background: 'rgba(0,0,0,.45)', borderRadius: 999, padding: '4px 8px' }}
            >
              <input type="checkbox" checked={recShowCards} onChange={(e) => setRecShowCards(e.target.checked)} />
              Cards
            </label>
          )}

          {/* Error indicator - tooltips don't exist on touch, so tapping
              shows the actual error text, then re-offers the join button */}
          {error && !isConnected && (
            <button
              className="mobile-error-btn"
              onClick={() => { alert(`Voice/Video error: ${error}`); setShowJoinPrompt(true); }}
              title={error}
            ><AlertTriangle size={20} /></button>
          )}

          {/* Connected Controls */}
          {isConnected && (
            <>
              {/* Minimized state - just show expand button with status */}
              {isMinimized ? (
                <button 
                  className={`mobile-expand-pill ${anyoneSpeaking ? 'speaking' : ''}`}
                  onClick={handleToggleMinimize}
                  title="Expand media controls"
                >
                  <span className={`pill-status-dot ${anyoneSpeaking ? 'speaking' : ''}`} />
                  <span className="pill-count">{totalParticipants}</span>
                </button>
              ) : (
                /* Expanded controls */
                <div className="mobile-controls-stack">
                  {/* Video toggle (hidden on voice-only tournament tables) */}
                  {mediaMode !== 'voice' && (
                  <button
                    className={`mobile-ctrl-btn ${isVideoOn ? 'active-green' : ''}`}
                    onClick={toggleVideo}
                    title={isVideoOn ? 'Camera Off' : 'Camera On'}
                  >
                    {isVideoOn ? <Video size={20} /> : <VideoOff size={20} />}

                  </button>
                  )}

                  {/* Flip camera - native only (front/back), shown while the
                      camera is on */}
                  {isVideoOn && isNativePlatform && (
                    <button
                      className="mobile-ctrl-btn"
                      onClick={switchCamera}
                      title="Flip camera"
                    ><SwitchCamera size={20} /></button>
                  )}

                  {/* Mute toggle */}
                  <button
                    className={`mobile-ctrl-btn ${isMuted ? 'active-red' : ''} ${isLocalSpeaking && !isMuted ? 'speaking' : ''}`}
                    onClick={toggleMute}
                    title={isMuted ? 'Unmute' : 'Mute'}
                  >
                    {isMuted ? <MicOff size={20} /> : <Mic size={20} />}

                  </button>

                  {/* Deafen toggle */}
                  <button
                    className={`mobile-ctrl-btn ${isDeafened ? 'active-orange' : ''}`}
                    onClick={toggleDeafen}
                    title={isDeafened ? 'Undeafen' : 'Deafen All'}
                  >
                    {isDeafened ? <VolumeX size={20} /> : <Headphones size={20} />}

                  </button>

                  {/* Background settings (only if video on) */}
                  {isVideoOn && isBackgroundSupported && (
                    <button
                      className={`mobile-ctrl-btn ${hasActiveBackground ? 'has-effect' : ''}`}
                      onClick={handleToggleBackgroundSettings}
                      title="Background Effects"
                    ><Sparkles size={20} /></button>
                  )}

                  {/* Leave button */}
                  <button
                    className="mobile-ctrl-btn leave"
                    onClick={handleLeave}
                    title="Leave Call"
                  ><PhoneOff size={20} /></button>

                  {/* Minimize button */}
                  <button
                    className="mobile-ctrl-btn minimize"
                    onClick={handleToggleMinimize}
                    title="Minimize"
                  ><ChevronDown size={20} /></button>
                </div>
              )}
            </>
          )}
        </div>

        {/* Background Settings Panel (mobile positioned) */}
        {isConnected && showBackgroundSettings && (
          <BackgroundSettingsPanel
            isVideoOn={isVideoOn}
            backgroundSettings={backgroundSettings}
            isBackgroundSupported={isBackgroundSupported}
            isBackgroundLoading={isBackgroundLoading}
            videoQuality={videoQuality}
            deviceCapabilities={deviceCapabilities}
            onSetBlur={setBackgroundBlur}
            onSetColor={setBackgroundColor}
            onSetImage={setBackgroundImage}
            onClear={clearBackground}
            onSetQuality={setVideoQuality}
            onClose={() => setShowBackgroundSettings(false)}
          />
        )}
      </>
    );
  }

  // ============================================================================
  //  DESKTOP RENDER - With minimize/collapse functionality

  // ============================================================================
  
  // 🆕 MINIMIZED STATE FOR DESKTOP - Just show a small pill
  if (isMinimized && isConnected) {
    return (
      <div className="media-chat-bar-minimized">
        <button 
          className={`desktop-expand-pill ${anyoneSpeaking ? 'speaking' : ''}`}
          onClick={handleToggleMinimize}
          title="Expand media controls"
        >
          <span className={`pill-status-dot ${anyoneSpeaking ? 'speaking' : ''}`} />
          <span className="pill-icon"><Mic size={14} /></span>

          <span className="pill-count">{totalParticipants}</span>
        </button>
      </div>
    );
  }

  if (mediaBlocked) {
    return (
      <div className="media-chat-bar left-side" style={{ padding: '6px 10px', fontSize: 12, color: '#fca5a5' }}>
        Camera and mic are off while this table is recorded.
      </div>
    );
  }

  return (
    <div className={`media-chat-bar left-side ${isConnected ? 'connected' : ''} ${isExpanded ? 'expanded' : ''}`}>
      {/* Free-minute countdown pill, transient warnings, paywall */}
      {voiceNotice && <div className="media-voice-notice">{voiceNotice}</div>}
      {isConnected && voiceAccess?.mode === 'free' && (
        <div className={`media-free-pill live ${freeRemaining !== null && freeRemaining <= 120 ? 'urgent' : ''}`} title="Free voice minutes left this week">
          <Clock size={12} /> {formatSeconds(freeRemaining ?? 0)}
        </div>
      )}
      {showPaywall && (
        <div className="media-actions-backdrop" onClick={() => setShowPaywall(false)}>
          <div className="media-actions-sheet" onClick={(e) => e.stopPropagation()}>
            <div className="media-actions-title">Free voice minutes used up for this week</div>
            <div className="media-actions-sub">
              Everyone gets {Math.round((voiceAccess?.weeklySeconds ?? 2700) / 60)} free minutes a week.
              Yours reset {voiceAccess ? formatResetDay(voiceAccess.resetsAtMs) : 'Monday'}. Keep talking now:
            </div>
            {paywallNotice && <div className="media-actions-notice">{paywallNotice}</div>}
            <button className="media-actions-btn primary" disabled={!!paywallBusy} onClick={handleBuyPass}>
              <span>24-hour unlimited pass</span>
              <span className="media-actions-price">{paywallBusy === 'pass' ? '...' : '$6.00 PSS Coins'}</span>
            </button>
            <button className="media-actions-btn primary" disabled={!!paywallBusy} onClick={handleUnlockTable}>
              <span>Unlock this table for everyone</span>
              <span className="media-actions-price">{paywallBusy === 'table' ? '...' : '$2.99 PSS Coins'}</span>
            </button>
            <button className="media-actions-btn subtle" onClick={() => setShowPaywall(false)}>Not now</button>
          </div>
        </div>
      )}
      {/* Block / report sheet for a participant */}
      {actionTarget && (
        <div className="media-actions-backdrop" onClick={closeParticipantActions}>
          <div className="media-actions-sheet" onClick={(e) => e.stopPropagation()}>
            <div className="media-actions-title">{getDisplayName(actionTarget)}</div>
            {actionNotice && <div className="media-actions-notice">{actionNotice}</div>}
            {actionMode === 'menu' ? (
              <>
                <button className="media-actions-btn" disabled={actionBusy} onClick={handleBlockToggle}>
                  <Ban size={16} />
                  {actionTarget.odid && blockedUsers.has(actionTarget.odid) ? 'Unblock player' : 'Block player'}
                </button>
                <button className="media-actions-btn" onClick={() => setActionMode('report')}>
                  <Flag size={16} />
                  Report player
                </button>
                <button className="media-actions-btn subtle" onClick={closeParticipantActions}>Cancel</button>
              </>
            ) : (
              <>
                <div className="media-actions-sub">Why are you reporting {getDisplayName(actionTarget)}?</div>
                <div className="media-actions-reasons">
                  {REPORT_REASONS.map((r) => (
                    <button
                      key={r.value}
                      className={`media-actions-reason ${reportReason === r.value ? 'active' : ''}`}
                      onClick={() => setReportReason(r.value)}
                    >
                      {r.label}
                    </button>
                  ))}
                </div>
                <textarea
                  className="media-actions-details"
                  placeholder="What happened? (optional)"
                  value={reportDetails}
                  onChange={(e) => setReportDetails(e.target.value)}
                  maxLength={500}
                />
                {evidencePreview && (
                  <div className="media-actions-evidence">
                    <img src={evidencePreview} alt="Screenshot attached" />
                    <span>Screenshot of the table attached</span>
                    <button onClick={clearEvidence}>Remove</button>
                  </div>
                )}
                <button className="media-actions-btn danger" disabled={!reportReason || actionBusy} onClick={handleSubmitReport}>
                  Submit report
                </button>
                <button className="media-actions-btn subtle" onClick={() => setActionMode('menu')}>Back</button>
              </>
            )}
          </div>
        </div>
      )}
      {/* Background Settings Panel */}
      {isConnected && showBackgroundSettings && (
        <BackgroundSettingsPanel
          isVideoOn={isVideoOn}
          backgroundSettings={backgroundSettings}
          isBackgroundSupported={isBackgroundSupported}
          isBackgroundLoading={isBackgroundLoading}
          videoQuality={videoQuality}
          deviceCapabilities={deviceCapabilities}
          onSetBlur={setBackgroundBlur}
          onSetColor={setBackgroundColor}
          onSetImage={setBackgroundImage}
          onClear={clearBackground}
          onSetQuality={setVideoQuality}
          onClose={() => setShowBackgroundSettings(false)}
        />
      )}

      {/* Video Grid - Shows when expanded and has video */}
      {isConnected && showVideoGrid && !isMinimized && (
        <div className={`video-grid ${
          participants.length === 0 ? 'single' : 
          participants.length <= 2 ? 'small' : 
          participants.length <= 5 ? 'medium' : 'large'
        }`}>
          {/* Local Video (the recorder has no camera: skip its empty tile) */}
          {!recorderMode && (
          <LocalVideoPlayer 
            videoTrack={localVideoTrack} 
            isMuted={isMuted}
            isVideoOn={isVideoOn}
            isSpeaking={isLocalSpeaking}
            username={currentUsername}
            isNativePlatform={isNativePlatform}
            setupNativeVideoView={setupNativeVideoView}
          />
          )}
          
          {/* Remote Videos - use Agora UID as key */}
          {participants.map((p) => (
            <RemoteVideoPlayer
              key={p.uid}
              participant={p}
              username={getDisplayName(p)}
              onMuteUser={muteRemoteUser}
              onHideVideo={hideRemoteUserVideo}
              onOpenActions={openParticipantActions}
              isNativePlatform={isNativePlatform}
              setupNativeVideoView={setupNativeVideoView}
            />
          ))}
        </div>
      )}

      {/* Join Prompt - Show when not connected */}
      {!isConnected && showJoinPrompt && !recorderMode && (
        <div className="media-join-prompt">
          <button
            className="media-join-btn"
            onClick={handleJoin}
            disabled={isConnecting}
            title="Join Voice/Video Chat"
          >
            {isConnecting ? (
              <>
                <span className="media-spinner" />
                <span className="media-btn-text">Connecting...</span>
              </>
            ) : (
              <>
                <span className="media-btn-icon"><Mic size={18} /></span>

                <span className="media-btn-text">Join Voice/Video</span>
              </>
            )}
          </button>
          {onJoinAndRecord && recordingOffer?.enabled && (
            <button
              className="media-join-btn"
              style={{ background: 'rgba(239,68,68,.18)', border: '1px solid rgba(239,68,68,.6)' }}
              disabled={isConnecting}
              title="Join voice/video and record this session"
              onClick={async () => {
                setRecNotice(null);
                await handleJoin();
                try { await onJoinAndRecord(recShowCards); } catch (e: any) { setRecNotice(e?.message || 'Could not start recording'); }
              }}
            >
              <span className="media-btn-icon" style={{ color: '#f87171' }}>●</span>
              <span className="media-btn-text">Join + Record · {recordingOffer.label}</span>
            </button>
          )}
          {onJoinAndRecord && recordingOffer?.enabled && (
            <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: '#cbd5e1', cursor: 'pointer', marginTop: 4 }}>
              <input type="checkbox" checked={recShowCards} onChange={(e) => setRecShowCards(e.target.checked)} />
              Show my cards in the recording
            </label>
          )}
          {accessLabel && (
            <div className={`media-free-pill ${voiceAccess?.mode === 'exhausted' ? 'urgent' : ''}`}>{accessLabel}</div>
          )}
          {recNotice && <div className="media-free-pill urgent">{recNotice}</div>}
        </div>
      )}

      {/* Error State */}
      {error && !isConnected && (
        <div className="media-error">
          <span> {error}</span>

          <button onClick={() => setShowJoinPrompt(true)}>Retry</button>
        </div>
      )}

      {/* Connected State - Media Controls */}
      {isConnected && (
        <div className="media-controls">
          {/* 🆕 Minimize Button - Collapse to pill */}
          <button
            className="media-control-btn minimize-btn"
            onClick={handleToggleMinimize}
            title="Minimize controls"
          >
            ◀
          </button>

          {/* Participant Count - Click to expand list */}
          <button 
            className={`media-participants-btn ${isLocalSpeaking && !isMuted ? 'speaking' : ''}`}
            onClick={() => setIsExpanded(!isExpanded)}
          >
            <span className="media-icon"></span>

            <span className="media-count">{totalParticipants}</span>
            <span className="media-label">in call</span>
          </button>

          {/* Video Grid Toggle */}
          <button
            className={`media-control-btn ${showVideoGrid ? 'active' : ''}`}
            onClick={handleToggleVideoGrid}
            title={showVideoGrid ? 'Hide Videos' : 'Show Videos'}
          >
            <LayoutGrid size={20} />
          </button>

          {/* Background Settings Toggle */}
          <button
            className={`media-control-btn ${showBackgroundSettings ? 'active' : ''} ${hasActiveBackground ? 'has-effect' : ''}`}
            onClick={handleToggleBackgroundSettings}
            title="Background Effects"
          >
            <Sparkles size={20} />
          </button>

          {/* Video Toggle (hidden on voice-only tournament tables) */}
          {mediaMode !== 'voice' && (
          <button
            className={`media-control-btn ${isVideoOn ? 'active-green' : ''}`}
            onClick={toggleVideo}
            title={isVideoOn ? 'Turn Off Camera' : 'Turn On Camera'}
          >
            {isVideoOn ? <Video size={20} /> : <VideoOff size={20} />}

          </button>
          )}

          {/* Camera Switch (native only) */}
          {isNativePlatform && isVideoOn && (
            <button
              className="media-control-btn"
              onClick={switchCamera}
              title="Switch Camera"
            >
              <SwitchCamera size={20} />
            </button>
          )}

          {/* Mute Button - with speaking indicator */}
          <button
            className={`media-control-btn ${isMuted ? 'active' : ''} ${isLocalSpeaking && !isMuted ? 'speaking' : ''}`}
            onClick={toggleMute}
            title={isMuted ? 'Unmute' : 'Mute'}
          >
            {isMuted ? <MicOff size={20} /> : <Mic size={20} />}

          </button>

          {/* Deafen Button */}
          <button
            className={`media-control-btn ${isDeafened ? 'active' : ''}`}
            onClick={toggleDeafen}
            title={isDeafened ? 'Undeafen' : 'Deafen'}
          >
            {isDeafened ? <VolumeX size={20} /> : <Headphones size={20} />}

          </button>

          {/* Leave Button */}
          <button
            className="media-leave-btn"
            onClick={handleLeave}
            title="Leave Call"
          ><PhoneOff size={20} /></button>
        </div>
      )}

      {/* Expanded Participant List */}
      {isConnected && isExpanded && !showVideoGrid && !showBackgroundSettings && (
        <div className="media-participant-list">
          {/* Self */}
          <div className={`participant-item self ${isLocalSpeaking && !isMuted ? 'speaking' : ''}`}>
            <span className={`participant-indicator ${isLocalSpeaking && !isMuted ? 'active' : ''}`} />
            <span className="participant-name">
              {currentUsername} (You)
              {isMuted && ' '}

              {isVideoOn && ' '}

              {hasActiveBackground && ' '}

            </span>
            {isLocalSpeaking && !isMuted && (
              <span className="speaking-waves"></span>

            )}
          </div>
          
          {/* Remote Participants - use Agora UID as key */}
          {participants.map((p) => (
            <div 
              key={p.uid}
              className={`participant-item ${p.isSpeaking ? 'speaking' : ''} ${p.isMutedByMe ? 'muted-by-me' : ''}`}
            >
              <span className={`participant-indicator ${p.isSpeaking ? 'active' : ''}`} />
              <span className="participant-name">
                {getDisplayName(p)}
                {p.odid && blockedUsers.has(p.odid) ? ' (blocked)' : p.isMutedByMe ? ' (muted by you)' : ''}
                {p.hasVideo && ' '}

              </span>
              {p.isSpeaking && !p.isMutedByMe && (
                <span className="speaking-waves"></span>

              )}
            </div>
          ))}
        </div>
      )}

      {/* Connection Status Indicator */}
      {isConnected && (
        <div className={`media-status-indicator ${isLocalSpeaking && !isMuted ? 'speaking' : ''}`}>
          <span className={`status-dot connected ${isLocalSpeaking && !isMuted ? 'speaking' : ''}`} />
          <span className="status-text">
            {isLocalSpeaking && !isMuted ? 'Speaking' : (isVideoOn ? 'Voice & Video Connected' : 'Voice Connected')}
          </span>
        </div>
      )}
    </div>
  );
};

export default MediaChatBar;